import { useEffect, useState, type ReactNode } from "react";
import { PLATFORMS } from "../lib/platforms";
import type { Rpc } from "../lib/rpc";
import type { PresenceState } from "../lib/control";

export type View =
  | { kind: "platform"; platform: string }
  | { kind: "settings"; page: SettingsPage }
  | { kind: "empty" };

export type SettingsPage =
  | "content"
  | "partners"
  | "queue"
  | "jobs"
  | "config"
  | "credentials"
  | "captioning";

const SETTINGS_NAV: Array<{ page: SettingsPage; icon: string; label: string }> = [
  { page: "content",     icon: "🎬", label: "Content" },
  { page: "partners",    icon: "👥", label: "Partners" },
  { page: "queue",       icon: "📦", label: "Queue" },
  { page: "jobs",        icon: "⚙️", label: "Jobs" },
  { page: "config",      icon: "📋", label: "Config" },
  { page: "credentials", icon: "🔑", label: "Credentials" },
  { page: "captioning",  icon: "✍️", label: "Captioning" },
];

interface Props {
  view: View;
  electronConnected: boolean;
  loginStates: Record<string, boolean>;
  // Set of platform keys (lowercase) that are paused in the active
  // account's Promo Config. Tabs stay clickable but get a muted look.
  disabledByConfig?: Set<string>;
  accountSwitcher: ReactNode;
  onSelectPlatform: (platform: string) => void;
  onSelectSettings: (page: SettingsPage) => void;
  // Status block in the sidebar footer — mirrors Electron's bottom-left
  // service rows (Proxy / CDP / LLaVA / Cloud).
  rpc: Rpc;
  controlConnected: boolean;
  presence: PresenceState | null;
  peerState: RTCPeerConnectionState | null;
}

export function Sidebar({
  view,
  electronConnected,
  loginStates,
  disabledByConfig,
  accountSwitcher,
  onSelectPlatform,
  onSelectSettings,
  rpc,
  controlConnected,
  presence,
  peerState,
}: Props) {
  const activePlatform = view.kind === "platform" ? view.platform : null;
  const activeSettings = view.kind === "settings" ? view.page : null;

  // ── Worker-side status that mirrors Electron's sidebar-footer rows.
  // Pulled over RPC since these live on the Electron process, not in the
  // signaling presence payload.
  const [llava, setLlava] = useState<string>("—");
  const [cdpPort, setCdpPort] = useState<number | string>("—");
  const [proxyOn, setProxyOn] = useState<boolean | null>(null);

  useEffect(() => {
    if (!electronConnected) {
      setLlava("—"); setCdpPort("—"); setProxyOn(null);
      return;
    }
    let cancelled = false;
    async function tick() {
      try {
        const [l, c, pcfg] = await Promise.all([
          rpc.call<string>("get-llava-status").catch(() => null),
          rpc.call<{ port: number }>("get-cdp-info").catch(() => null),
          rpc.call<{ enabled?: boolean }>("get-proxy-config").catch(() => null),
        ]);
        if (cancelled) return;
        if (l) setLlava(l);
        if (c?.port) setCdpPort(c.port);
        if (pcfg) setProxyOn(!!pcfg.enabled);
      } catch { /* swallow */ }
    }
    tick();
    const offLlava = rpc.onEvent<string>("llava-status", (s) => setLlava(s));
    const handle = window.setInterval(tick, 15000); // slow poll for cdp/proxy
    return () => { cancelled = true; window.clearInterval(handle); offLlava(); };
  }, [rpc, electronConnected]);

  const llavaClass =
    llava === "ready" ? "ok" :
    llava === "loading" ? "loading" :
    llava === "off" ? "off" : "muted";
  const llavaLabel =
    llava === "ready" ? "Ready" :
    llava === "loading" ? "Loading…" :
    llava === "off" ? "Off" : "—";

  const peerClass =
    peerState === "connected" ? "ok" :
    peerState === "failed" ? "off" :
    peerState ? "loading" : "muted";

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <div className="brand">fan-management</div>
        <div className={`presence ${electronConnected ? "ok" : "off"}`}>
          {electronConnected ? "● Live view ready" : "○ Electron offline"}
        </div>
        {accountSwitcher}
      </div>

      <nav className="platform-nav">
        {PLATFORMS.map((p) => {
          const loggedIn = loginStates[p.key];
          const showBadge = electronConnected && loggedIn === false;
          const offForAccount = !!disabledByConfig?.has(p.key);
          const classes = [
            "platform-btn",
            activePlatform === p.key ? "active" : "",
            offForAccount ? "disabled-by-config" : "",
          ].filter(Boolean).join(" ");
          const title = !electronConnected
            ? "Start Electron on the worker PC to enable"
            : offForAccount
              ? `${p.name} is disabled for this account — toggle it in Config`
              : `Live-view ${p.name}`;
          return (
            <button
              key={p.key}
              className={classes}
              onClick={() => onSelectPlatform(p.key)}
              disabled={!electronConnected}
              title={title}
            >
              <span className="icon" style={{ color: p.color }}>{p.icon}</span>
              <span className="name">{p.name}</span>
              {showBadge && <span className="notif-dot" title="Login required">!</span>}
            </button>
          );
        })}
      </nav>

      <div className="settings-nav">
        {SETTINGS_NAV.map((item) => (
          <button
            key={item.page}
            className={`settings-btn ${activeSettings === item.page ? "active" : ""}`}
            onClick={() => onSelectSettings(item.page)}
            disabled={!electronConnected}
            title={electronConnected ? item.label : "Electron must be running"}
          >
            <span className="icon">{item.icon}</span>
            <span className="name">{item.label}</span>
          </button>
        ))}
      </div>

      {/* Service / status block — mirrors Electron's sidebar-footer. Lives
          at the bottom of the sidebar via margin-top: auto in CSS. */}
      <div className="sidebar-footer">
        <div className="footer-info">
          <span className="label">Control</span>
          <span className={`value ${controlConnected ? "ok" : "off"}`}>
            {controlConnected ? "Online" : "Offline"}
          </span>
        </div>
        <div className="footer-info">
          <span className="label">Electron</span>
          <span className={`value ${presence?.electronConnected ? "ok" : "off"}`}>
            {presence?.electronConnected ? "Online" : "Offline"}
          </span>
        </div>
        <div className="footer-info" title={peerState ?? "no peer"}>
          <span className="label">Live view</span>
          <span className={`value ${peerClass}`}>
            {peerState === "connected" ? "Live" :
             peerState === "connecting" ? "Connecting…" :
             peerState === "failed" ? "Failed" :
             peerState ? peerState : "Idle"}
          </span>
        </div>
        <div className="footer-info">
          <span className="label">LLaVA</span>
          <span className={`value ${llavaClass}`}>{llavaLabel}</span>
        </div>
        <div className="footer-info">
          <span className="label">CDP Port</span>
          <span className="value">{cdpPort}</span>
        </div>
        <div className="footer-info">
          <span className="label">Proxy</span>
          <span className={`value ${proxyOn ? "ok" : "off"}`}>
            {proxyOn === null ? "—" : proxyOn ? "ON" : "OFF"}
          </span>
        </div>
      </div>
    </aside>
  );
}
